import { Component, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PredictorModelState } from 'src/app/state/predictor-model/predictor-model.state';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { TerrainInspectDetails } from '../../models/TerrainInspectDetails';
import { CanvasService } from '../../services/canvas.service';
import { InteractionType } from '../../enums/interaction-type';

@Component({
  selector: 'app-map-detail-modal',
  templateUrl: './map-detail-modal.component.html',
  styleUrls: ['./map-detail-modal.component.scss'],
})
export class MapDetailModalComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  @Select(PredictorModelState.inspectDetails)
  inspectDetails$!: Observable<TerrainInspectDetails>;

  inspectDetails!: TerrainInspectDetails;
  inspectHasAgent = false;

  agentDetails: { label: string; value: string | number | boolean }[] = [];
  eventCountDetails: { label: string; value: string | number | boolean | undefined }[] = [];

  constructor(private store: Store, public canvasService: CanvasService) {}

  /**
   * Lifecycle hook - Initializes the component and subscribes to inspection details.
   */
  ngOnInit(): void {
    this.inspectHasAgent = false;

    this.inspectDetails$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((inspectDetails) => {
      this.inspectDetails = inspectDetails;
      this.setEventCountDetails(inspectDetails);
      this.setAgentDetails(inspectDetails);
    });
  }

  /**
   * Lifecycle hook - Cleans up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Sets details about events if available.
   * @param inspectDetails The inspection details object.
   */
  private setEventCountDetails(inspectDetails: TerrainInspectDetails): void {
    if (inspectDetails.eventCount != null) {
      this.eventCountDetails = [
        { label: 'Events', value: inspectDetails.eventCount },
        { label: 'Min. Depth', value: inspectDetails.minDepth ?? '' },
        { label: 'Max. Depth', value: inspectDetails.maxDepth ?? '' },
      ];
    } else {
      this.eventCountDetails = [];
    }
  }

  /**
   * Sets agent details if an agent exists and is marked as dead.
   * @param inspectDetails The inspection details object.
   */
  private setAgentDetails(inspectDetails: TerrainInspectDetails): void {
    if (inspectDetails.agent?.isDead) {
      this.inspectHasAgent = true;
      this.agentDetails = [
        { label: 'Agent Mass', value: `${inspectDetails.agent.mass ?? ''} m` },
        { label: 'Facing Direction', value: inspectDetails.agent.facing ?? 'N/A' },
        { label: 'Direction Change', value: inspectDetails.agent.directionChange ?? 'N/A' },
        { label: 'Spawn Level', value: inspectDetails.agent.spawnLevel ?? 'N/A' },
        { label: 'Age', value: inspectDetails.agent.age ?? 'N/A' },
        { label: 'Is Dead?', value: inspectDetails.agent.isDead ?? 'N/A' },
      ];
    } else {
      this.inspectHasAgent = false;
      this.agentDetails = [];
    }
  }

  /**
   * Toggles the visibility of map details and updates interaction settings.
   */
  toggleDetails(): void {
    const showDetails: boolean = this.store.selectSnapshot(PredictorModelState.showDetails);

    this.canvasService.togglePanning(showDetails);
    this.store.dispatch(new PredictorModelActions.SetInteractionType(InteractionType.None));
    this.store.dispatch(new PredictorModelActions.SetDetailVisibility(!showDetails));
  }
}
